import { useFragment } from "@/generated";
import {
  GetProjectByParentIdDocument,
  GetProjectsForNavigationDocument,
  ProjectFieldsFragmentDoc,
} from "@/generated/graphql";
import {
  type ProjectsByParentOptions,
  getProjectsByParentIdVars,
} from "@/lib/utils/graphql/getProjectsByParentIdVars";
import { useQuery } from "@apollo/client/react";

/**
 * Fetches and paginates projects belonging to a specific parent project.
 *
 * Queries the GraphQL API for projects under a parent project with support for
 * infinite scrolling/pagination, search, and sorting. Results are cached (cache-first)
 * for performance. The loadMore function appends additional pages to the existing list.
 *
 * @param options - Configuration options for the hook
 * @param options.parentId - The unique identifier of the parent project
 * @param options.searchText - Optional search query to filter projects by name
 * @param options.sortBy - Optional sort order (e.g., "ALPHABETICAL" or default to most recent)
 * @param options.skip - If true, skips the query execution (default: false)
 * @param options.limit - Optional page size, overriding the shared default
 * @returns An object containing:
 *   - projects: Array of Project objects for the current page(s)
 *   - hasMore: Boolean indicating if more projects are available to load
 *   - loading: Boolean indicating if the initial query is in progress
 *   - loadMore: Function to fetch the next page of projects
 *
 * @example
 * const { projects, hasMore, loading, loadMore } = useProjectsForNavigation({
 *   parentId: "parent123",
 *   searchText: "science"
 * });
 */
export function useProjectsForNavigation({
  parentId,
  searchText,
  sortBy,
  isTrash = false,
  skip = false,
  limit,
}: ProjectsByParentOptions) {
  const {
    data: projectsByParentIdData,
    loading,
    fetchMore,
  } = useQuery(GetProjectsForNavigationDocument, {
    variables: getProjectsByParentIdVars({
      parentId,
      searchText,
      sortBy,
      isTrash,
      limit,
    }),
    skip,
    fetchPolicy: "cache-first",
  });

  const items = projectsByParentIdData?.projectsByParentId.items ?? [];
  const hasMore = projectsByParentIdData?.projectsByParentId.hasMore ?? false;

  const loadMore = () => {
    fetchMore({
      variables: {
        ...getProjectsByParentIdVars({
          parentId,
          searchText,
          sortBy,
          isTrash,
          limit,
        }),
        offset: items.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          projectsByParentId: {
            ...fetchMoreResult.projectsByParentId,
            items: [...prev.projectsByParentId.items, ...fetchMoreResult.projectsByParentId.items],
          },
        };
      },
    });
  };

  return {
    projects: items,
    hasMore,
    loading,
    loadMore,
  };
}
